import React, { useEffect, useRef, useState } from "react";
import { useStyles } from "./marketAccess.module";

const cards = [
  {
    id: "originators",
    title: "For Originators",
    description: "Taking LATAM projects from zero to high-quality CDR assets.",
    items: [
      "Technical Biomass Screening",
      "Pre-Seed Financing",
      "Global Demand Connection",
    ],
    icon: "🌿",
    accentKey: "green",
  },
  {
    id: "investors",
    title: "For Investors",
    description:
      "Access to verified assets, reputational risk mitigation, and compliance.",
    items: [
      "Instant Digital Due Diligence",
      "High-Integrity Portfolios",
      "Regulatory Compliance",
    ],
    icon: "📊",
    accentKey: "blue",
  },
];

const TiltCard = ({ card, classes }) => {
  const cardRef = useRef(null);
  const innerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [glarePos, setGlarePos] = useState({ x: 50, y: 50 });
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotateX = ((y - cy) / cy) * -5;
    const rotateY = ((x - cx) / cx) * 5;
    innerRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
    setGlarePos({ x: (x / rect.width) * 100, y: (y / rect.height) * 100 });
  };

  const handleMouseLeave = () => {
    innerRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
    setHovered(false);
  };

  const isGreen = card.accentKey === "green";

  return (
    <div
      ref={cardRef}
      className={`${classes.tiltCard} ${visible ? classes.visible : ""}`}
      style={{ animationDelay: isGreen ? "0.1s" : "0.2s" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={() => setHovered(true)}
    >
      <div
        ref={innerRef}
        className={`${classes.tiltInner} ${
          isGreen ? classes.cardGreen : classes.cardBlue
        }`}
      >
        <div
          className={classes.glare}
          style={{
            background: `radial-gradient(circle at ${glarePos.x}% ${glarePos.y}%, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 70%)`,
            opacity: hovered ? 1 : 0,
          }}
        />

        <div
          className={`${classes.iconWrapper} ${
            isGreen ? classes.iconGreen : classes.iconBlue
          }`}
        >
          <span className={classes.iconEmoji}>{card.icon}</span>
        </div>

        <h3 className={classes.cardTitle}>{card.title}</h3>

        <p className={classes.cardDescription}>{card.description}</p>

        <ul className={classes.itemList}>
          {card.items.map((item, i) => (
            <li key={i} className={classes.itemRow}>
              <span
                className={`${classes.checkIcon} ${
                  isGreen ? classes.checkGreen : classes.checkBlue
                }`}
              >
                ✓
              </span>
              <span className={classes.itemText}>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const MarketAccess = () => {
  const classes = useStyles();

  return (
    <section className={classes.section}>
      <div className={classes.header}>
        <p className={classes.overline}>MARKET ACCESS</p>
      </div>
      <div className={classes.grid}>
        {cards.map((card) => (
          <TiltCard key={card.id} card={card} classes={classes} />
        ))}
      </div>
    </section>
  );
};

export default MarketAccess;
